import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import './moderation.css';
import { StatusState } from '../Editorial.jsx';
import { formatDateTime, labelFor, mySuspensionStatus, submitAppeal, targetTypeLabels, validateAppeal } from './moderation.js';

const appealStatusText = {
  open: 'Your appeal is waiting for a decision.',
  upheld: 'Your appeal was reviewed and the decision stands.',
  overturned: 'Your appeal was successful.',
};

/** Lets a person see any suspension or removal that affects them and appeal it once. */
export function AppealPage() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let live = true;
    if (!user) return undefined;
    mySuspensionStatus(supabase).then((value) => { if (live) { setStatus(value); setError(''); } }).catch((cause) => { if (live) setError(cause.message); });
    return () => { live = false; };
  }, [user, version]);

  if (loading) return <div className="content-wrap">Loading account…</div>;
  if (!user) return <div className="rs-container rs-state-page"><StatusState icon="shieldCheck" kicker="ACCOUNT STATUS" title="Sign in to see your account status"><div><Link className="rs-button is-large" to="/sign-in?next=%2Fappeal">Sign in</Link></div></StatusState></div>;
  const reload = () => setVersion((value) => value + 1);
  const suspensions = status?.suspensions || [];
  const removals = status?.removals || [];

  return (
    <div className="content-wrap account-page">
      <header className="coord-page-heading">
        <div>
          <span className="account-kicker">ACCOUNT STATUS</span>
          <h1>Suspensions and appeals.</h1>
          <p>If we have suspended your account or removed something you posted, you can ask us to look again. Each decision can be appealed once.</p>
        </div>
      </header>
      {error && <div role="alert"><p className="mod-error">{error}</p><button className="button button-secondary" onClick={reload}>Try again</button></div>}
      {!status && !error && <p>Loading…</p>}
      {status && suspensions.length === 0 && removals.length === 0 && <p className="account-empty">Your account is in good standing. There is nothing to appeal.</p>}
      {suspensions.length > 0 && (
        <section className="account-content-panel">
          <h2>{suspensions.some((row) => row.scope === 'person') ? 'Your account is suspended' : 'Your company is suspended'}</h2>
          <p>While suspended you cannot post jobs, send bids or send messages.</p>
          <ul className="mod-list">
            {suspensions.map((row) => (
              <li key={row.id}>
                <strong>{row.scope === 'company' ? `Company: ${row.organization_name || 'your company'}` : 'Your account'}</strong>
                <p><b>Reason:</b> {row.reason}</p>
                <p className="mod-note">Since {formatDateTime(row.starts_at)}{row.ends_at ? `, until ${formatDateTime(row.ends_at)}` : ', with no end date set'}</p>
                <AppealArea appeal={row.appeal} canAppeal={row.can_appeal} cannotText="Only a company owner or admin can appeal a company suspension."
                  onSubmit={(statement) => submitAppeal(supabase, { statement, suspensionId: row.id })} onDone={reload} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {removals.length > 0 && (
        <section className="account-content-panel">
          <h2>Content we removed</h2>
          <ul className="mod-list">
            {removals.map((row) => (
              <li key={row.action_id}>
                <strong>{labelFor(targetTypeLabels, row.target_type)}{row.title ? `: ${row.title}` : ''}</strong>
                <p><b>Reason:</b> {row.reason}</p>
                <p className="mod-note">Removed {formatDateTime(row.removed_at)}</p>
                <AppealArea appeal={row.appeal} canAppeal onSubmit={(statement) => submitAppeal(supabase, { statement, actionId: row.action_id })} onDone={reload} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default AppealPage;

function AppealArea({ appeal, canAppeal, cannotText, onSubmit, onDone }) {
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (appeal) {
    return (
      <div className="mod-form">
        <strong>{appealStatusText[appeal.status] || 'Appeal received.'}</strong>
        <span className="mod-note">Sent {formatDateTime(appeal.created_at)}{appeal.decided_at ? ` · decided ${formatDateTime(appeal.decided_at)}` : ''}</span>
        {appeal.decision_note && <p>{appeal.decision_note}</p>}
      </div>
    );
  }
  if (!canAppeal) return <p className="mod-note">{cannotText}</p>;
  async function submit(event) {
    event.preventDefault();
    const problem = validateAppeal(statement);
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    try { await onSubmit(statement); setStatement(''); onDone(); } catch (cause) { setError(cause.message); } finally { setBusy(false); }
  }
  return (
    <form className="mod-form" onSubmit={submit}>
      <label>Why should we look again?<textarea value={statement} onChange={(e) => setStatement(e.target.value)} maxLength={4000} disabled={busy} /></label>
      <span className="mod-note">You can appeal each decision once. A member of our team will review it.</span>
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="mod-action-row"><button className="button button-blue" disabled={busy}>{busy ? 'Sending…' : 'Send appeal'}</button></div>
    </form>
  );
}
